import React, { useCallback, useEffect, useState } from 'react';
import { Box, Modal, VStack } from 'native-base';
import {
    AuthPayload,
    rememberMeApi,
    useAppSettings,
    useAuthPin,
    useCheckAuth,
    useI18n,
    useRequestPin,
    useSpringBigStore,
} from '@springbig/shared';
import { PinFormData } from './components/PinForm';
import { Caption } from '~/shared/Typography';
import { useGeneralStyles } from '~/shared/styles';
import { AppRouteProps, useAppNavigate } from '..';
import { useLocation } from 'react-router';
import Button from '~/shared/Button';
import { BirthdayStep } from '../tenDLC/components/BirthdayStep';
import { WelcomeStep } from '../tenDLC/components/WelcomeStep';
import { CreateStep } from '../tenDLC/components/CreateStep';
import LoginTemplate from './LoginTemplate';
import FooterTemplate from './FooterTemplate';
import { AuthTemplate } from './AuthTemplate';

const SharedPinScreen = () => {

    const [stashboardOrMemberKey, setStashboardOrMemberKey] = useState<
        AuthPayload['reference']
    >();
    const [disabled, setDisabled] = useState(false);

    const {
        walletId,
        setWalletId,
        setBirthdays,
        setNextStep,
        stashboardTarget,
        setBirthdayInput,
        apiEnv,
        merchantName, 
        setMerchantName,
        setTendlcLogoSrc
    } = useSpringBigStore();

    const { mutate: checkAuth } = useCheckAuth();
    const { mutate: authPin } = useAuthPin();
    const { mutate: requestPin, error: requestPinError } = useRequestPin();

    const { navigate, params } = useAppNavigate();
    const location = useLocation();
    const gStyle = useGeneralStyles();
    const [pinError, setPinError] = useState('');
    const { defaultScreen } = useAppSettings();
    const [codeLength, setCodeLength] = useState(4);
    const [showBirthdayModal, setShowBirthdayModal] = useState(false);
    const { t } = useI18n();
    const [body, setBody] = useState<'login' | 'welcome' | 'create'>('login');
    const [countdown, setCountdown] = useState<number>(0);
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [showCreateModal, setShowCreateModal] = useState(false);
  
    useEffect(() => {
      let timer;
      if (countdown > 0) {
        timer = setInterval(() => {
          setCountdown((prevCountdown) => prevCountdown - 1);
        }, 1000);
      } else {
        setDisabled(false);
        clearInterval(timer);
      }
  
      return () => clearInterval(timer);
    }, [countdown]);

    const [isResendCode, setIsResendCode] = useState(false);

    const handleResendCode = () => {

        const _params =
            stashboardOrMemberKey === 'stashboard'
                ? params.stashboard_key
                : params.wallet_id;

        _params && createRequestPin(_params!);
        setDisabled(true);
        setCountdown(30);
        setIsResendCode(true);
    };

    useEffect(() => {

        let timer;

        if (disabled) {
            timer = setTimeout(() => {
                setDisabled(false);
            }, 60 * 1000);

            return () => clearTimeout(timer);
        }
    }, [disabled]);

    const handleSubmit = useCallback(
        (values: PinFormData) => {

            setPinError('');
            if (stashboardOrMemberKey) {
                const _params =
                    stashboardOrMemberKey === 'stashboard'
                        ? params.stashboard_key
                        : params.wallet_id;

                authPin({
                    stashboardOrMemberKey: _params!,
                    pin: btoa(values.pin),
                    source: 'shared_pin',
                    reference: stashboardOrMemberKey!,
                    merger_wallet_id: new URLSearchParams(location.search).get('merger_wallet_id')
                }, {
                    onSuccess: (response) => {

                        if (response.data) {
                            setBirthdays(response.data);
                            setBirthdayInput(response.ten_dlc_birthday_input);
                        }

                        setMerchantName(response?.merchant_dba_name);
                        setTendlcLogoSrc(response?.tendlc_logo_src);
                        setNextStep(response.step);
                        if (response.step === 'birthday' && response.data) {
                            setShowBirthdayModal(true);
                        } else if (response.step === 'create') {
                            setShowCreateModal(true);
                        } else {
                            onNavigate(response.ten_dlc_flow);
                        }
                    },
                    onError: (error: any) => {

                        if (error.status === 401 || error.status === 422) {
                            setPinError(t('sharedPin.errors.invalidCode'));
                        }
                    }
                });

            }
        }, [stashboardOrMemberKey, params, authPin]
    );

    const getAuthMember = useCallback(
        (id: string, reference: AuthPayload['reference']) => {

            checkAuth({
                stashboardOrMemberKey: id,
                reference,
                skip_tendlc: new URLSearchParams(location.search).get("skip10dlc") ? new URLSearchParams(location.search).get("skip10dlc") === 'true' : undefined,
                merger_wallet_id: new URLSearchParams(location.search).get('merger_wallet_id')
            }, {
                onSuccess: (response) => {

                    if (response.data) {
                        setBirthdays(response.data);
                        setBirthdayInput(response.ten_dlc_birthday_input);
                    }

                    setMerchantName(response?.merchant_dba_name);
                    setTendlcLogoSrc(response?.tendlc_logo_src);

                    setNextStep(response.step);
                    if (response.step === 'birthday' && response.data) {
                        setShowBirthdayModal(true);
                    } else if (response.step === 'create') {
                        setShowCreateModal(true);
                    } else {
                        onNavigate(response.ten_dlc_flow);
                    }
                },
                onError: (error: any) => {

                    if (error.status === 401) {
                        setCodeLength(parseInt(error.data.pin_size));
                        if (parseInt(error.data.pin_size) === 6) {
                            setBody("create");
                        }

                        setWalletId(error.data.merchant_member_key);
                        setMerchantName(error.data.merchant_dba_name);
                        setTendlcLogoSrc(error.data?.tendlc_logo_src);
                        setPinError('');
                    }
                }
            });

        },
        [setBirthdays, setNextStep, setCodeLength]
    );

    const onNavigate = (isTenDlc: boolean): void => {

        const messageTarget = stashboardTarget ? `messages/${stashboardTarget}` : defaultScreen;

        setWalletId(params.wallet_id);
        let target = isTenDlc ? `w/${params.wallet_id}/tendlc?merchantName=${merchantName}` : `w/${params.wallet_id}/${(messageTarget || 'messages')}`;

        const plan_id = localStorage.getItem('plan_id');
        const link_id = localStorage.getItem('link_id');

        if (plan_id) {
            localStorage.removeItem('plan_id');
            target = `w/${params.wallet_id}/subscription/${plan_id}`;
        }
        else if (link_id) {
            target = `w/${params.wallet_id}/subscriptions`;
        }

        navigate(target as AppRouteProps);
    };

    const createRequestPin = useCallback(
        (id: string) => {

            id && requestPin({
                stashboardOrMemberKey: id,
                reference: stashboardOrMemberKey!,
                pin_length: codeLength,
            }, {
                onSuccess(data) {
                    if (data) {
                        setPhoneNumber(data.phone);
                    }
                },
            });
        },
        [stashboardOrMemberKey, requestPin, codeLength]
    );

    useEffect(() => {
        document.title = 'Secure SMS Viewer | Log in';
        (document as any).description = 'Access your messages';
    }, []);

    useEffect(() => {

        if (location?.state?.isStashboard && walletId) {
            navigate(`w/${walletId}`, { state: { from: location.pathname, isUnAuthorised: true, isStashboard: false } });
        }
        else if (location?.state?.isStashboard && params && params.wallet_id) {
            getAuthMember(params.wallet_id, 'stashboard');
            setStashboardOrMemberKey('stashboard');
        }
        else if (params && params.wallet_id) {
            getAuthMember(params.wallet_id, 'member');
            setStashboardOrMemberKey('member');
        }
        else if (params && params.stashboard_key) {
            getAuthMember(params.stashboard_key, 'stashboard');
            setStashboardOrMemberKey('stashboard');
        }
    }, [getAuthMember, params.stashboard_key, params.wallet_id, rememberMeApi]);

    useEffect(
        () => {
            if (requestPinError) {

                navigate('404');
            }
        },
        [requestPinError, navigate]
    );

    const handleChildEvent = (message: string): void => {

        if (params && params.wallet_id) {
            setWalletId(params.wallet_id);
        }

        switch (message) {
            case 'messages':
                let target = stashboardTarget ? `w/${params.wallet_id}/messages/${stashboardTarget}` : `w/${params.wallet_id}/messages`;
                const plan_id = localStorage.getItem('plan_id');
                if (plan_id) {
                    localStorage.removeItem('plan_id');
                    target = `w/${params.wallet_id}/subscription/${plan_id}`;
                }

                const link_id = localStorage.getItem('link_id');
                if (link_id) {
                    localStorage.removeItem('link_id');
                    target = `w/${params.wallet_id}/subscriptions`;
                }

                navigate(target as AppRouteProps);
                break;
            case 'welcome':
                setShowBirthdayModal(false);
                setShowCreateModal(false);
                setBody('welcome');
                break;
        }
    };
    
  const loginBody = (
    <LoginTemplate
      codeLength={codeLength}
      handleSubmit={handleSubmit}
      pinError={pinError}
      disabled={disabled}
      phoneNumber={phoneNumber}
      countdown={countdown}
      handleResendCode={handleResendCode}
      isResendCode={isResendCode}
    />
  );

  const welcomeBody = (
    <>
      <WelcomeStep t={t} onEvent={handleChildEvent} />
    </>
  );

  const createBody = (
    <VStack justifyContent={'flex-start'} flex={1} space={4}>
      <VStack>
        <Caption
          fontSize="24"
          fontWeight="bold"
          fontFamily={'Helvetica'}
          color={gStyle.text}
        >
          {t('sharedPin.create.title')}
        </Caption>
        <Caption fontFamily={'Helvetica'} color={gStyle.text}>
          {t('sharedPin.create.description')}
        </Caption>
        <Button
          fontFamily={'Helvetica'}
          bg={gStyle.button_background}
          style={{ opacity: disabled ? 0.5 : 1 }}
          _text={{ color: gStyle.button_text }}
          isDisabled={disabled}
          borderRadius={'12px'}
          onPress={() => {
            handleResendCode();
            setBody('login');
          }}
          mt={4}
        >
          {t('sharedPin.create.button')}
        </Button>
      </VStack>
      <FooterTemplate />
    </VStack>
  );

  const getBody = () => {
    if (body === 'login') {
        return loginBody;
    }

    if (body === 'welcome') {
        return welcomeBody;
    }

    if (body === 'create') {
        return createBody;
    }

    return null;
  };

  return (
    <AuthTemplate
      body={
        <VStack flex={1}>
          {getBody()}
          {showBirthdayModal && (
            <Modal isOpen={showBirthdayModal} backgroundColor="rgba(0, 0, 0, 0.8)">
              <Box bg={gStyle.screen_background} p={8} borderRadius={'lg'}>
                <BirthdayStep
                  t={t}
                  onEvent={handleChildEvent}
                  navigate={navigate}
                  params={params}
                />
              </Box>
            </Modal>
          )}
          {showCreateModal && (
            <Modal isOpen={showCreateModal} backgroundColor="rgba(0, 0, 0, 0.8)">
              <Box backgroundColor={gStyle.screen_background} p={8} borderRadius={'lg'}>
                <CreateStep
                  t={t}
                  onEvent={handleChildEvent}
                  navigate={navigate}
                  params={params}
                />
              </Box>
            </Modal>
          )}
        </VStack>
      }
    />
  );
};

export default SharedPinScreen;
